import { z } from 'zod';

export const AlgorithmId = z.enum(['perlin', 'simplex', 'worley', 'value']);
export const ModifierId = z.enum(['none', 'ridged', 'terrace', 'domain-warp']);
export const RenderMode = z.enum(['2d', '3d-smooth', '3d-voxel']);

// Iterable source of truth for resolution values. Keep the Zod union below in
// sync if this tuple changes (the union is declared explicitly rather than
// derived so Zod's typing stays readable).
export const RESOLUTION_VALUES = [128, 256, 512, 1024] as const;
export const Resolution = z.union([z.literal(128), z.literal(256), z.literal(512), z.literal(1024)]);

export const NoiseParamsSchema = z.object({
  schemaVersion: z.literal(1).default(1),
  seed: z.union([z.string().min(1).max(64), z.number().int()]),
  algorithm: AlgorithmId,
  resolution: Resolution,
  scale: z.number().min(1).max(500),
  octaves: z.number().int().min(1).max(8),
  persistence: z.number().min(0).max(1),
  lacunarity: z.number().min(1).max(4),
  modifier: ModifierId,
  warpStrength: z.number().min(0).max(100),
  terraceSteps: z.number().int().min(2).max(32),
  waterLevel: z.number().min(0).max(1),
  elevationScale: z.number().min(0).max(1),
}).passthrough(); // forward-compat: preserve unknown fields

export type NoiseParams = z.infer<typeof NoiseParamsSchema>;

export const DEFAULT_NOISE_PARAMS: NoiseParams = {
  schemaVersion: 1,
  seed: 'world',
  algorithm: 'simplex',
  resolution: 256,
  scale: 100,
  octaves: 5,
  persistence: 0.5,
  lacunarity: 2.0,
  modifier: 'none',
  warpStrength: 30,
  terraceSteps: 8,
  waterLevel: 0.4,
  elevationScale: 0.3,
};

export const PresetSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(80),
  paramsJson: z.string(),
  paletteId: z.string().min(1),
  ownerToken: z.string().min(1),
  createdAt: z.number().int(),
});
export type Preset = z.infer<typeof PresetSchema>;

export const CreatePresetSchema = z.object({
  name: z.string().min(1).max(80),
  params: NoiseParamsSchema,
  paletteId: z.string().min(1),
});
export type CreatePreset = z.infer<typeof CreatePresetSchema>;

export const WorldSchema = z.object({
  id: z.string().min(1),
  paramsJson: z.string(),
  paletteId: z.string().min(1),
  renderMode: RenderMode,
  viewCount: z.number().int().min(0),
  createdAt: z.number().int(),
});
export type World = z.infer<typeof WorldSchema>;

export const CreateWorldSchema = z.object({
  params: NoiseParamsSchema,
  paletteId: z.string().min(1),
  renderMode: RenderMode,
});
export type CreateWorld = z.infer<typeof CreateWorldSchema>;

export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;
